import React, { useState, useMemo, useEffect } from 'react';
import {
  LayoutDashboard, Sparkles, Upload, BookOpen, Settings, Languages,
  BookMarked, ScrollText, LogOut, Heart, BarChart2, Bot, Mail,
  Image as ImageIcon, Globe, CalendarClock, LineChart, ChevronDown, Radar,
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const DASHBOARD = { id: 'dashboard', label: 'Översikt', icon: LayoutDashboard };

const GROUPS = [
  { id: 'content', label: 'Innehåll', emoji: '📝', items: [
    { id: 'generate',   label: 'Generera',    icon: Sparkles },
    { id: 'bulk',       label: 'Massimport',  icon: Upload },
    { id: 'translate',  label: 'Översätt',    icon: Languages },
    { id: 'vocabulary', label: 'Vokabulär',   icon: BookMarked },
    { id: 'templates',  label: 'Mallar',      icon: BookOpen },
    { id: 'favorites',  label: 'Favoriter',   icon: Heart },
  ] },
  { id: 'publishing', label: 'Publicering', emoji: '🚀', items: [
    { id: 'images',     label: 'Bildhanterare', icon: ImageIcon },
    { id: 'newsletter', label: 'Nyhetsbrev',    icon: Mail },
    { id: 'storyblok',  label: 'Storyblok',     icon: Globe, soon: true },
  ] },
  { id: 'automation', label: 'Automation', emoji: '⚡', items: [
    { id: 'autopilot',  label: 'Autopilot',     icon: Bot },
    { id: 'scheduler',  label: 'Schemaläggare', icon: CalendarClock, soon: true },
  ] },
  { id: 'analytics', label: 'Analys & Planering', emoji: '📊', items: [
    { id: 'metastats',  label: 'Meta Statistik', icon: Radar },
    { id: 'marketplan', label: 'Marknadsplan',  icon: LineChart, soon: true },
  ] },
  { id: 'system', label: 'System', emoji: '🔧', items: [
    { id: 'logs',       label: 'Loggar',        icon: ScrollText },
    { id: 'admin',      label: 'Admin',         icon: BarChart2, adminOnly: true },
    { id: 'settings',   label: 'Inställningar', icon: Settings },
  ] },
];

// Hitta vilken grupp som innehåller en given page-id
function findGroupForPage(pageId) {
  for (const g of GROUPS) {
    if (g.items.some(it => it.id === pageId)) return g.id;
  }
  return null;
}

export default function Sidebar({ activePage, onNavigate, user, isAdmin }) {
  // Filtrera bort adminOnly-items om inte admin, och tomma grupper.
  const visibleGroups = useMemo(() => GROUPS
    .map(g => ({ ...g, items: g.items.filter(it => !it.adminOnly || isAdmin) }))
    .filter(g => g.items.length > 0),
  [isAdmin]);

  const [openGroupId, setOpenGroupId] = useState(() => findGroupForPage(activePage));

  // Säkerställ att aktiv sidas grupp alltid är öppen
  useEffect(() => {
    const g = findGroupForPage(activePage);
    if (g) setOpenGroupId(g);
  }, [activePage]);

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut();
  };

  const toggleGroup = (gid) => setOpenGroupId(prev => prev === gid ? null : gid);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src="/vv-logo.svg" alt="Vårdväskan" width={28} height={28}
             style={{ display: 'block', borderRadius: 6 }} />
        <div className="sidebar-logo-text">
          <span className="sidebar-logo-brand">Vårdväskan</span>
          <span className="sidebar-logo-sub">AI Studio</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        <p className="sidebar-section-label">Verktyg</p>

        {/* Dashboard — standalone */}
        <button
          key={DASHBOARD.id}
          data-testid={`nav-${DASHBOARD.id}`}
          className={`nav-item${activePage === DASHBOARD.id ? ' nav-item--active' : ''}`}
          onClick={() => onNavigate(DASHBOARD.id)}
        >
          <DASHBOARD.icon size={16} className="nav-icon" />
          <span>{DASHBOARD.label}</span>
        </button>

        {/* Accordion-grupper */}
        {visibleGroups.map(group => {
          const isOpen = openGroupId === group.id;
          return (
            <div key={group.id} className="nav-group">
              <button
                type="button"
                data-testid={`nav-group-${group.id}`}
                className={`nav-group-header${isOpen ? ' nav-group-header--open' : ''}`}
                onClick={() => toggleGroup(group.id)}
                aria-expanded={isOpen}
              >
                <span className="nav-group-emoji">{group.emoji}</span>
                <span className="nav-group-label">{group.label}</span>
                <ChevronDown
                  size={14}
                  className={`nav-group-chevron${isOpen ? ' nav-group-chevron--open' : ''}`}
                />
              </button>

              <div className={`nav-group-children${isOpen ? ' nav-group-children--open' : ''}`}>
                {group.items.map(({ id, label, icon: Icon, soon }) => {
                  // Admins kan klicka på "Snart"-items (badge behålls som indikator).
                  // Vanliga användare ser dem som låsta (oklickbara).
                  if (soon && !isAdmin) {
                    return (
                      <div
                        key={id}
                        data-testid={`nav-soon-${id}`}
                        className="nav-item nav-item--child nav-item--soon"
                        aria-disabled="true"
                      >
                        <Icon size={16} className="nav-icon" />
                        <span>{label}</span>
                        <span className="nav-soon-badge">Snart</span>
                      </div>
                    );
                  }
                  return (
                    <button
                      key={id}
                      data-testid={`nav-${id}`}
                      className={`nav-item nav-item--child${activePage === id ? ' nav-item--active' : ''}`}
                      onClick={() => onNavigate(id)}
                    >
                      <Icon size={16} className="nav-icon" />
                      <span>{label}</span>
                      {soon && <span className="nav-soon-badge">Snart</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        {user && (
          <div className="sidebar-user">
            <span className="sidebar-user-email" title={user.email}>{user.email}</span>
            <button
              className="sidebar-logout-btn"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Logga ut"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
        <span className="sidebar-version">Beta v1.0</span>
      </div>
    </aside>
  );
}
